import type { Question, Skill, Topic } from '@/data/types';

/** Logarytmy i funkcja wykładnicza. Materiał autorski, niezweryfikowany wobec CKE. */

export const LOG_TOPIC: Topic = {
  id: 'math-logarithms',
  subjectId: 'math',
  name: 'Logarytmy i funkcja wykładnicza',
};

export const LOG_SKILLS: Skill[] = [
  {
    id: 'log-basic',
    topicId: 'math-logarithms',
    name: 'Definicja logarytmu',
    ckeRequirement: 'Logarytmy — definicja i obliczanie',
    prerequisites: [],
    examValue: 0.7,
  },
  {
    id: 'log-properties',
    topicId: 'math-logarithms',
    name: 'Działania na logarytmach',
    ckeRequirement: 'Logarytmy — własności działań',
    prerequisites: ['log-basic'],
    examValue: 0.75,
  },
  {
    id: 'exp-equations',
    topicId: 'math-logarithms',
    name: 'Równania wykładnicze',
    ckeRequirement: 'Funkcja wykładnicza — równania',
    prerequisites: ['log-basic'],
    examValue: 0.7,
  },
];

export const LOG_QUESTIONS: Question[] = [
  // --- log-basic --------------------------------------------------------
  {
    id: 'q-log-b-1',
    skillId: 'log-basic',
    kind: 'foundation',
    prompt: 'Oblicz $\\log_2 32$.',
    format: 'numeric',
    answer: '5',
    acceptedVariants: [],
    solution: '$\\log_2 32$ to wykładnik, do którego trzeba podnieść $2$, aby otrzymać $32$. Ponieważ $2^5 = 32$, wynik to $5$.',
    hints: [
      { level: 1, text: 'Do której potęgi trzeba podnieść podstawę, żeby dostać liczbę logarytmowaną?' },
      { level: 2, text: 'Szukasz wykładnika, nie wyniku dzielenia.' },
      { level: 3, text: 'Definicja: $\\log_a b = c$ wtedy i tylko wtedy, gdy $a^c = b$.' },
      { level: 5, text: 'Wypisz kolejne potęgi dwójki i znajdź tę równą $32$.' },
    ],
    commonErrors: [
      {
        id: 'log-dzielenie',
        matches: ['16'],
        cause: 'Logarytm potraktowany jak dzielenie — policzone $32 : 2$.',
        rule: 'Logarytm to wykładnik potęgi, nie iloraz.',
      },
    ],
    difficulty: 1,
    source: 'FORGE / autorskie',
    verified: false,
  },
  {
    id: 'q-log-b-2',
    skillId: 'log-basic',
    kind: 'typical',
    prompt: 'Oblicz $\\log_3 \\dfrac{1}{9}$.',
    format: 'numeric',
    answer: '-2',
    acceptedVariants: [],
    solution: 'Szukamy $c$ takiego, że $3^c = \\tfrac{1}{9}$. Ponieważ $3^{-2} = \\tfrac{1}{9}$, wynik to $-2$.',
    hints: [
      { level: 1, text: 'Czy wykładnik może być ujemny? Co wtedy dzieje się z potęgą?' },
      { level: 2, text: 'Liczba logarytmowana jest mniejsza od jedynki, więc wykładnik jest ujemny.' },
      { level: 3, text: 'Zachodzi $a^{-n} = \\dfrac{1}{a^{n}}$.' },
      { level: 5, text: 'Zapisz $\\tfrac{1}{9}$ jako potęgę trójki z ujemnym wykładnikiem.' },
    ],
    commonErrors: [
      {
        id: 'log-znak-ujemny',
        matches: ['2'],
        cause: 'Pominięty znak minus — policzone $\\log_3 9$ zamiast $\\log_3 \\tfrac{1}{9}$.',
        rule: 'Dla argumentu mniejszego od jedynki logarytm o podstawie większej od jedynki jest ujemny.',
      },
    ],
    difficulty: 2,
    source: 'FORGE / autorskie',
    verified: false,
  },
  {
    id: 'q-log-b-3',
    skillId: 'log-basic',
    kind: 'typical',
    prompt: 'Rozwiąż równanie $\\log_5 x = 3$.',
    format: 'numeric',
    answer: '125',
    acceptedVariants: ['x=125'],
    solution: 'Z definicji logarytmu $x = 5^3 = 125$.',
    hints: [
      { level: 1, text: 'Co mówi definicja logarytmu o związku podstawy, wyniku i argumentu?' },
      { level: 2, text: 'Zamień zapis logarytmiczny na wykładniczy.' },
      { level: 3, text: '$\\log_a x = c$ oznacza $x = a^{c}$.' },
      { level: 5, text: 'Podnieś podstawę do potęgi równej prawej stronie równania.' },
    ],
    commonErrors: [
      {
        id: 'log-mnozenie',
        matches: ['15'],
        cause: 'Podstawa pomnożona przez wynik zamiast podniesienia do potęgi.',
        rule: 'Z $\\log_a x = c$ wynika $x = a^c$, nie $x = a\\cdot c$.',
      },
    ],
    difficulty: 3,
    source: 'FORGE / autorskie',
    verified: false,
  },
  {
    id: 'q-log-b-4',
    skillId: 'log-basic',
    kind: 'transfer',
    prompt: 'Oblicz $\\log_{\\frac{1}{2}} 8$.',
    format: 'numeric',
    answer: '-3',
    acceptedVariants: [],
    solution:
      'Szukamy $c$ takiego, że $\\left(\\tfrac{1}{2}\\right)^c = 8$. Ponieważ $\\left(\\tfrac{1}{2}\\right)^{-3} = 2^{3} = 8$, wynik to $-3$.',
    hints: [
      { level: 1, text: 'Podstawa jest mniejsza od jedynki. Jak to wpływa na znak wyniku?' },
      { level: 2, text: 'Zapisz podstawę jako potęgę dwójki z ujemnym wykładnikiem.' },
      { level: 3, text: '$\\left(\\tfrac{1}{2}\\right)^{c} = 2^{-c}$.' },
      { level: 5, text: 'Z równania $2^{-c} = 2^{3}$ porównaj wykładniki.' },
    ],
    commonErrors: [
      {
        id: 'log-podstawa-ulamkowa',
        matches: ['3'],
        cause: 'Pominięty wpływ ułamkowej podstawy — policzone tak, jakby podstawą była dwójka.',
        rule: 'Dla podstawy z przedziału $(0,1)$ logarytm liczby większej od jedynki jest ujemny.',
      },
    ],
    difficulty: 4,
    source: 'FORGE / autorskie',
    verified: false,
  },

  // --- log-properties ---------------------------------------------------
  {
    id: 'q-log-p-1',
    skillId: 'log-properties',
    kind: 'foundation',
    prompt: 'Oblicz $\\log_2 8 + \\log_2 4$.',
    format: 'numeric',
    answer: '5',
    acceptedVariants: [],
    solution: '$\\log_2 8 = 3$ oraz $\\log_2 4 = 2$, więc suma wynosi $5$. Równoważnie $\\log_2 (8\\cdot 4) = \\log_2 32$.',
    hints: [
      { level: 1, text: 'Czy potrafisz policzyć każdy z tych logarytmów osobno?' },
      { level: 2, text: 'Oba argumenty są potęgami dwójki.' },
      { level: 3, text: 'Suma logarytmów o tej samej podstawie to logarytm iloczynu.' },
      { level: 5, text: 'Dodaj otrzymane wykładniki.' },
    ],
    commonErrors: [
      {
        id: 'log-mnozenie-logarytmow',
        matches: ['6'],
        cause: 'Logarytmy pomnożone zamiast dodane.',
        rule: 'Zapis $\\log a + \\log b$ oznacza dodawanie wyników, a odpowiada $\\log(ab)$.',
      },
    ],
    difficulty: 2,
    source: 'FORGE / autorskie',
    verified: false,
  },
  {
    id: 'q-log-p-2',
    skillId: 'log-properties',
    kind: 'typical',
    prompt: 'Oblicz $\\log_3 54 - \\log_3 2$.',
    format: 'numeric',
    answer: '3',
    acceptedVariants: [],
    solution: '$\\log_3 54 - \\log_3 2 = \\log_3 \\dfrac{54}{2} = \\log_3 27 = 3$.',
    hints: [
      { level: 1, text: 'Czy da się połączyć te dwa logarytmy w jeden?' },
      { level: 2, text: 'Różnica logarytmów o tej samej podstawie to logarytm ilorazu.' },
      { level: 3, text: '$\\log_a x - \\log_a y = \\log_a \\dfrac{x}{y}$.' },
      { level: 5, text: 'Podziel argumenty, a potem policz logarytm otrzymanej liczby.' },
    ],
    commonErrors: [
      {
        id: 'log-argument-zamiast-wyniku',
        matches: ['27'],
        cause: 'Podany argument logarytmu zamiast jego wartości.',
        rule: 'Po uproszczeniu do $\\log_3 27$ trzeba jeszcze policzyć sam logarytm.',
      },
    ],
    difficulty: 3,
    source: 'FORGE / autorskie',
    verified: false,
  },
  {
    id: 'q-log-p-3',
    skillId: 'log-properties',
    kind: 'typical',
    prompt: 'Oblicz $\\log_2 8^{2}$.',
    format: 'numeric',
    answer: '6',
    acceptedVariants: [],
    solution: '$\\log_2 8^{2} = 2\\log_2 8 = 2\\cdot 3 = 6$.',
    hints: [
      { level: 1, text: 'Co dzieje się z wykładnikiem argumentu wewnątrz logarytmu?' },
      { level: 2, text: 'Wykładnik można przenieść przed logarytm.' },
      { level: 3, text: '$\\log_a x^{k} = k\\log_a x$.' },
      { level: 5, text: 'Policz $\\log_2 8$, a wynik pomnóż przez wykładnik.' },
    ],
    commonErrors: [
      {
        id: 'log-potega-argumentu',
        matches: ['64'],
        cause: 'Policzony argument $8^2$ zamiast wartości logarytmu.',
        rule: 'Pytanie dotyczy logarytmu, nie samej liczby pod nim.',
      },
      {
        id: 'log-pominiety-wykladnik',
        matches: ['3'],
        cause: 'Wykładnik zignorowany — policzone $\\log_2 8$.',
        rule: 'Wykładnik argumentu mnoży wartość logarytmu.',
      },
    ],
    difficulty: 3,
    source: 'FORGE / autorskie',
    verified: false,
  },
  {
    id: 'q-log-p-4',
    skillId: 'log-properties',
    kind: 'transfer',
    prompt: 'Oblicz $\\log_6 2 + \\log_6 3$.',
    format: 'numeric',
    answer: '1',
    acceptedVariants: [],
    solution:
      'Suma logarytmów to logarytm iloczynu: $\\log_6 (2\\cdot 3) = \\log_6 6 = 1$.',
    hints: [
      { level: 1, text: 'Żadnego z tych logarytmów nie policzysz osobno. Co z tego wynika?' },
      { level: 2, text: 'Połącz je w jeden logarytm i zobacz, co wyjdzie w argumencie.' },
      { level: 3, text: '$\\log_a x + \\log_a y = \\log_a (xy)$.' },
      { level: 5, text: 'Iloczyn argumentów równa się podstawie. Ile wynosi $\\log_a a$?' },
    ],
    commonErrors: [
      {
        id: 'log-argument-iloczynu',
        matches: ['6'],
        cause: 'Podany iloczyn argumentów zamiast wartości logarytmu.',
        rule: '$\\log_a a = 1$ — argument równy podstawie daje jedynkę.',
      },
    ],
    difficulty: 4,
    source: 'FORGE / autorskie',
    verified: false,
  },

  // --- exp-equations ----------------------------------------------------
  {
    id: 'q-exp-1',
    skillId: 'exp-equations',
    kind: 'foundation',
    prompt: 'Rozwiąż równanie $2^{x} = 16$.',
    format: 'numeric',
    answer: '4',
    acceptedVariants: ['x=4'],
    solution: '$16 = 2^4$, więc $2^x = 2^4$ i stąd $x = 4$.',
    hints: [
      { level: 1, text: 'Czy prawą stronę da się zapisać jako potęgę tej samej podstawy?' },
      { level: 2, text: 'Zapisz obie strony jako potęgi dwójki.' },
      { level: 3, text: 'Gdy podstawy są równe i różne od jedynki, wykładniki też muszą być równe.' },
      { level: 5, text: 'Wypisz kolejne potęgi dwójki i znajdź tę równą prawej stronie.' },
    ],
    commonErrors: [
      {
        id: 'exp-dzielenie',
        matches: ['8'],
        cause: 'Równanie wykładnicze potraktowane jak liniowe — policzone $16 : 2$.',
        rule: 'W $2^x = 16$ niewiadoma stoi w wykładniku, więc dzielenie nic nie daje.',
      },
    ],
    difficulty: 2,
    source: 'FORGE / autorskie',
    verified: false,
  },
  {
    id: 'q-exp-2',
    skillId: 'exp-equations',
    kind: 'typical',
    prompt: 'Rozwiąż równanie $3^{\\,x+1} = 81$.',
    format: 'numeric',
    answer: '3',
    acceptedVariants: ['x=3'],
    solution: '$81 = 3^4$, więc $x + 1 = 4$, skąd $x = 3$.',
    hints: [
      { level: 1, text: 'Jak zapisać prawą stronę jako potęgę trójki?' },
      { level: 2, text: 'Po zrównaniu podstaw porównaj całe wykładniki.' },
      { level: 3, text: 'Wykładnikiem po lewej stronie jest $x+1$, a nie samo $x$.' },
      { level: 5, text: 'Rozwiąż równanie $x + 1 = 4$.' },
    ],
    commonErrors: [
      {
        id: 'exp-pominiete-przesuniecie',
        matches: ['4'],
        cause: 'Podany wykładnik zamiast $x$ — pominięte odjęcie jedynki.',
        rule: 'Z $x + 1 = 4$ wynika $x = 3$.',
      },
    ],
    difficulty: 3,
    source: 'FORGE / autorskie',
    verified: false,
  },
  {
    id: 'q-exp-3',
    skillId: 'exp-equations',
    kind: 'typical',
    prompt: 'Rozwiąż równanie $\\left(\\dfrac{1}{2}\\right)^{x} = 8$.',
    format: 'numeric',
    answer: '-3',
    acceptedVariants: ['x=-3'],
    solution:
      '$\\left(\\tfrac{1}{2}\\right)^{x} = 2^{-x}$, a $8 = 2^{3}$, więc $-x = 3$ i $x = -3$.',
    hints: [
      { level: 1, text: 'Jak zapisać ułamkową podstawę jako potęgę dwójki?' },
      { level: 2, text: '$\\tfrac{1}{2} = 2^{-1}$.' },
      { level: 3, text: 'Stąd lewa strona to $2^{-x}$.' },
      { level: 5, text: 'Porównaj wykładniki w równaniu $2^{-x} = 2^{3}$.' },
    ],
    commonErrors: [
      {
        id: 'exp-znak-wykladnika',
        matches: ['3'],
        cause: 'Pominięty minus wynikający z ułamkowej podstawy.',
        rule: '$\\left(\\tfrac{1}{a}\\right)^{x} = a^{-x}$.',
      },
    ],
    difficulty: 3,
    source: 'FORGE / autorskie',
    verified: false,
  },
  {
    id: 'q-exp-4',
    skillId: 'exp-equations',
    kind: 'transfer',
    prompt: 'Rozwiąż równanie $4^{x} = 8$.',
    format: 'numeric',
    answer: '1.5',
    acceptedVariants: ['3/2', 'x=1.5'],
    solution:
      'Sprowadzamy do wspólnej podstawy: $4^{x} = 2^{2x}$ oraz $8 = 2^{3}$, więc $2x = 3$ i $x = \\tfrac{3}{2}$.',
    hints: [
      { level: 1, text: 'Podstawy są różne. Czy mają wspólną podstawę, do której obie sprowadzisz?' },
      { level: 2, text: 'Zarówno $4$, jak i $8$ są potęgami dwójki.' },
      { level: 3, text: '$4^{x} = (2^{2})^{x} = 2^{2x}$.' },
      { level: 5, text: 'Z równania $2x = 3$ wyznacz $x$ — wynik nie musi być całkowity.' },
    ],
    commonErrors: [
      {
        id: 'exp-rozne-podstawy',
        matches: ['2'],
        cause: 'Policzone $8 : 4$ zamiast sprowadzenia obu stron do wspólnej podstawy.',
        rule: 'Wykładniki wolno porównywać dopiero po zrównaniu podstaw.',
      },
    ],
    difficulty: 4,
    source: 'FORGE / autorskie',
    verified: false,
  },
];
